import { Effect, Schema } from "effect";
import { HttpApiEndpoint, HttpApiGroup } from "effect/unstable/httpapi";
import { ObservabilityHttpErrorResponses } from "../errors/http.ts";
import { AgenticCacheResult, AgenticTraceContext, AgenticTracePhase, AGENTIC_CACHE_RESULTS,
  AGENTIC_TRACE_PHASES, traceAttributesFromContext } from "../agentic-correlation.ts";
import { TraceValidationError } from "../errors/observability.ts";
import { TraceListResult, TraceSpanRecord } from "../trace-model.ts";

/** Trace HTTP scopes preserve case-sensitive, reserved legacy resource slugs. */
export const TraceScopeId = /* @__PURE__ */ (() => Schema.String.check(
  Schema.isPattern(/^[a-z0-9][a-z0-9-]{0,63}$/)
).pipe(Schema.brand("trace-scope")))();
export type TraceScopeId = typeof TraceScopeId.Type;

// This transport historically accepts any 16-64 hexadecimal/hyphen characters.
// Probe correlation is a separate UUID-v4 contract.
export const TraceHttpId = /* @__PURE__ */ (() => Schema.String.check(
  Schema.isPattern(/^[0-9a-f-]{16,64}$/i)
).pipe(Schema.brand("trace-http-id")))();
export type TraceHttpId = typeof TraceHttpId.Type;

export const TraceListFilters = /* @__PURE__ */ (() => Schema.Struct({
  status: Schema.optionalKey(Schema.Literals(["ok", "error"])),
  component: Schema.optionalKey(Schema.String), operation: Schema.optionalKey(Schema.String),
  since: Schema.optionalKey(Schema.Int), before: Schema.optionalKey(Schema.Int),
  minDurationMs: Schema.optionalKey(Schema.Finite), limit: Schema.Int,
  attributeFilters: Schema.optionalKey(Schema.Record(Schema.String, Schema.Union([Schema.String, Schema.Number])))
}))();
export type TraceListFilters = typeof TraceListFilters.Type;

export const TraceListResponse = /* @__PURE__ */ (() => Schema.Struct({
  workspaceId: TraceScopeId, ...TraceListResult.fields, generatedAt: Schema.Number
}))();
export const TraceReadResponse = /* @__PURE__ */ (() => Schema.Struct({
  workspaceId: TraceScopeId, traceId: TraceHttpId, spans: Schema.Array(TraceSpanRecord), truncated: Schema.Boolean
}))();
export const TraceClearResponse = /* @__PURE__ */ (() => Schema.Struct({
  workspaceId: TraceScopeId, deleted: Schema.Number
}))();

const invalid = (message: string) => new TraceValidationError({ message });
const contextTextKeys = ["changeId", "taskId", "runId", "parentRunId", "attemptId", "worktreeId", "treeId",
  "inputTreeId", "outputTreeId", "candidateId", "checkId", "reviewId", "namespaceId"] as const;
export const TraceProbeId = /* @__PURE__ */ (() => Schema.String.check(
  Schema.isPattern(/^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i)
).pipe(Schema.brand("trace-probe")))();

// Preserve Number() wire coercion (hex, exponents and whitespace), then validate
// finite range before integrality so diagnostics keep their historical order.
export const traceNumberParam = Effect.fn("Trace.queryNumber")(function* (
  search: URLSearchParams, name: string, minimum: number, maximum: number, integer = true
) {
  const value = search.get(name);
  if (value === null || value === "") return undefined;
  const parsed = yield* Schema.decodeUnknownEffect(Schema.Finite.check(Schema.isBetween({ minimum, maximum })))(Number(value)).pipe(
    Effect.mapError(() => invalid(`${name} must be between ${minimum} and ${maximum}.`)));
  return integer ? yield* Schema.decodeUnknownEffect(Schema.Int)(parsed).pipe(
    Effect.mapError(() => invalid(`${name} must be an integer between ${minimum} and ${maximum}.`))) : parsed;
});
const textParam = Effect.fn("Trace.queryText")(function* (search: URLSearchParams, name: string, maximum: number) {
  const value = search.get(name)?.trim();
  if (!value) return undefined;
  return yield* Schema.decodeUnknownEffect(Schema.String.check(Schema.isMaxLength(maximum)))(value).pipe(
    Effect.mapError(() => invalid(`${name} must be at most ${maximum} characters.`)));
});
const enumParam = Effect.fn("Trace.queryEnum")(function* <A extends string>(
  search: URLSearchParams, name: string, schema: Schema.Codec<A, string>, values: readonly string[]
) {
  const value = search.get(name);
  if (value === null || value === "") return undefined;
  return yield* Schema.decodeUnknownEffect(schema)(value).pipe(
    Effect.mapError(() => invalid(`${name} must be one of: ${values.join(", ")}.`)));
});

export const traceListFilters = Effect.fn("Trace.listFilters")(function* (search: URLSearchParams) {
  const rawStatus = search.get("status");
  const status = rawStatus ? yield* Schema.decodeUnknownEffect(Schema.Literals(["ok", "error"]))(rawStatus).pipe(
    Effect.mapError(() => invalid("status must be ok or error."))) : undefined;
  const text = yield* Effect.forEach(contextTextKeys, key => textParam(search, key, 256).pipe(
    Effect.map(value => [key, value] as const)), { concurrency: 1 });
  const context = yield* Schema.decodeUnknownEffect(AgenticTraceContext)({
    ...Object.fromEntries(text.filter(([, value]) => value !== undefined)),
    fence: yield* traceNumberParam(search, "fence", 1, Number.MAX_SAFE_INTEGER),
    cacheResult: yield* enumParam(search, "cacheResult", AgenticCacheResult, AGENTIC_CACHE_RESULTS),
    eventSequence: yield* traceNumberParam(search, "eventSequence", 0, Number.MAX_SAFE_INTEGER),
    targetRef: yield* textParam(search, "targetRef", 128),
    phase: yield* enumParam(search, "phase", AgenticTracePhase, AGENTIC_TRACE_PHASES)
  }).pipe(Effect.mapError(error => invalid(error.message)));
  const probeText = yield* textParam(search, "traceProbeId", 36);
  const probeId = probeText === undefined ? undefined : yield* Schema.decodeUnknownEffect(TraceProbeId)(probeText).pipe(
    Effect.mapError(() => invalid("traceProbeId must be a UUID v4.")));
  const attributeFilters = { ...traceAttributesFromContext(context),
    ...(probeId === undefined ? {} : { "workerstation.trace_probe_id": probeId }) };
  const component = search.get("component");
  const operation = search.get("operation");
  const since = yield* traceNumberParam(search, "since", 0, Number.MAX_SAFE_INTEGER);
  const before = yield* traceNumberParam(search, "before", 0, Number.MAX_SAFE_INTEGER);
  const minDurationMs = yield* traceNumberParam(search, "minDurationMs", 0, 3_600_000, false);
  const limit = (yield* traceNumberParam(search, "limit", 1, 100)) ?? 50;
  return yield* Schema.decodeUnknownEffect(TraceListFilters)({
    ...(status ? { status } : {}), ...(component ? { component: component.slice(0, 160) } : {}),
    ...(operation ? { operation: operation.slice(0, 240) } : {}),
    ...(Object.keys(attributeFilters).length ? { attributeFilters } : {}),
    ...(since === undefined ? {} : { since }), ...(before === undefined ? {} : { before }),
    ...(minDurationMs === undefined ? {} : { minDurationMs }), limit
  }).pipe(Effect.mapError(error => invalid(error.message)));
});

// HttpApi exposes repeated values as arrays; compatibility normalization above
// deliberately uses the first URLSearchParams value, as the original route did.
export const TraceListQuery = /* @__PURE__ */ (() => {
  const text = Schema.optionalKey(Schema.Union([Schema.String, Schema.Array(Schema.String)]));
  return Schema.Struct({ stationId: text, status: text, component: text, operation: text,
    since: text, before: text, minDurationMs: text, limit: text, changeId: text, taskId: text, runId: text,
    parentRunId: text, attemptId: text, worktreeId: text, treeId: text, inputTreeId: text, outputTreeId: text,
    candidateId: text, checkId: text, reviewId: text, namespaceId: text, fence: text, cacheResult: text,
    eventSequence: text, targetRef: text, phase: text, traceProbeId: text });
})();
export const TraceReadQuery = /* @__PURE__ */ (() => Schema.Struct({
  stationId: TraceListQuery.fields.stationId, spanLimit: TraceListQuery.fields.limit
}))();

export const TraceApi = HttpApiGroup.make("traces").add(
  HttpApiEndpoint.get("list", "/api/traces/:workspaceId", {
    params: Schema.Struct({ workspaceId: Schema.String }), query: TraceListQuery,
    success: TraceListResponse, error: ObservabilityHttpErrorResponses
  }),
  HttpApiEndpoint.get("read", "/api/traces/:workspaceId/:traceId", {
    params: Schema.Struct({ workspaceId: Schema.String, traceId: Schema.String }), query: TraceReadQuery,
    success: TraceReadResponse, error: ObservabilityHttpErrorResponses
  }),
  HttpApiEndpoint.delete("clear", "/api/traces/:workspaceId", {
    params: Schema.Struct({ workspaceId: Schema.String }),
    query: Schema.Struct({ stationId: TraceListQuery.fields.stationId }),
    success: TraceClearResponse, error: ObservabilityHttpErrorResponses
  })
);
